import type { ScoProduct } from '@src/graphql-types'

import {
  isRecommendedProduct,
  PLAN_PITCHES,
  resolvePaywallIntent,
  visiblePaywallFeatures,
  type PaywallIntent,
  type PaywallIntentId,
} from './intent'

export function productLabel(product: ScoProduct): string {
  return (
    product.paywallOptions?.title?.trim() ||
    product.paywallName?.trim() ||
    product.title?.trim() ||
    product.productId?.trim() ||
    'Plan'
  )
}

export function formatCents(cents?: number | null): string {
  if (cents == null) {
    return 'Price unavailable'
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

export function productPricing(product: ScoProduct) {
  return {
    signup: product.effectivePricingInCents?.signup ?? product.pricingInCents?.signup,
    recurring: product.effectivePricingInCents?.recurring ?? product.pricingInCents?.recurring,
  }
}

export function productRenewalLabel(product: ScoProduct): string | null {
  const recurring = productPricing(product).recurring
  const termId = product.effectiveTermId || product.termId
  if (recurring == null || termId === 'one') {
    return null
  }
  const abbreviation = product.effectiveTerm?.abbreviation?.trim()
  const suffix = abbreviation ? `/${abbreviation}` : termId === 'month' ? '/mo' : ''
  return `${formatCents(recurring)}${suffix}`
}

export function productPriceLabel(product: ScoProduct): string {
  const { signup, recurring } = productPricing(product)
  if (signup == null) {
    return 'Price unavailable'
  }
  const amount = formatCents(signup)
  const termId = product.effectiveTermId || product.termId
  if (recurring != null && signup !== recurring && termId !== 'one') {
    const renewal = productRenewalLabel(product)
    if (renewal) {
      return `${amount} today, then ${renewal}`
    }
  }
  if (termId === 'month') {
    return `${amount}/mo`
  }
  const termName = product.effectiveTerm?.name?.trim()
  if (termName) {
    return `${amount} ${termName}`
  }
  return amount
}

/** Term lengths by id; the catalog's `months` field is not populated for membership terms. */
export const TERM_MONTHS: Record<string, number> = { month: 1, threeMonth: 3, sixMonth: 6, year: 12 }

const TERM_BILLING: Record<string, string> = {
  threeMonth: 'billed quarterly',
  sixMonth: 'billed every 6 months',
  year: 'billed annually',
}

/**
 * The price a plan tile leads with, split for type: the amount, its period, and one quieter line
 * (the renewal when today differs, or the monthly equivalent of a prepaid term). All of it is
 * arithmetic on the prices the API resolved onto this product.
 */
export function productPriceDisplay(product: ScoProduct): {
  amount: string
  period: string | null
  note: string | null
} {
  const { signup, recurring } = productPricing(product)
  const termId = product.effectiveTermId || product.termId || ''
  if (signup == null) {
    return { amount: 'Price unavailable', period: null, note: null }
  }
  if (termId === 'one' || recurring == null) {
    return { amount: formatCents(signup), period: null, note: termId === 'one' ? 'One-time purchase' : null }
  }
  const abbreviation = product.effectiveTerm?.abbreviation?.trim()
  const period = abbreviation ? `/${abbreviation}` : termId === 'month' ? '/mo' : null
  if (signup !== recurring) {
    const renewal = productRenewalLabel(product)
    return { amount: formatCents(signup), period: null, note: renewal ? `today, then ${renewal}` : null }
  }
  const months = TERM_MONTHS[termId]
  const monthly = months && months > 1 ? `${formatCents(Math.round(recurring / months))}/mo, ` : ''
  const billing = TERM_BILLING[termId]
  return {
    amount: formatCents(recurring),
    period: period ?? (product.effectiveTerm?.name?.trim() ? ` ${product.effectiveTerm.name.trim()}` : null),
    note: billing ? `${monthly}${billing}` : null,
  }
}

export function productCanContinue(product: ScoProduct): boolean {
  return Boolean(product.productId?.trim()) && productPricing(product).signup != null
}

/** Labels only prices and coupons already resolved onto this ScoProduct by the API. */
export function productPromotion(product: ScoProduct) {
  const pricing = productPricing(product)
  const code = product.appliedCoupon?.code?.trim()
  const lowerSignup =
    pricing.signup != null &&
    product.pricingInCents?.signup != null &&
    pricing.signup < product.pricingInCents.signup
  const lowerRecurring =
    pricing.recurring != null &&
    product.pricingInCents?.recurring != null &&
    pricing.recurring < product.pricingInCents.recurring
  const trialDays = product.effectivePricingInCents?.trialDays ?? product.pricingInCents?.trialDays

  if (!code && !lowerSignup && !lowerRecurring && !trialDays) {
    return null
  }

  return {
    label:
      trialDays && pricing.signup === 0
        ? `${trialDays}-day free trial`
        : code
          ? 'Offer applied'
          : 'Special offer',
    code,
    listPrice:
      lowerSignup || lowerRecurring ? productPriceLabel({ ...product, effectivePricingInCents: null }) : null,
  }
}

export function productDescription(product: ScoProduct): string | undefined {
  const description = product.paywallOptions?.description?.trim()
  return description || undefined
}

/**
 * Keep a single-product wall's job copy tied to the product it can sell. When the intent sells a
 * different plan, the wall pitches this plan's own value instead of a job it can't do.
 */
export function productPaywallIntent(
  product: ScoProduct,
  intent?: PaywallIntentId,
  tokens?: Record<string, string>
) {
  const resolved = resolvePaywallIntent(intent, tokens)
  if (resolved.recommendedProductId === product.productId) {
    return resolved
  }
  const pitch = (product.productId && PLAN_PITCHES[product.productId]) || {
    headline: `Upgrade to ${productLabel(product)}`,
    supporting: productDescription(product) || 'See what this plan includes.',
  }
  return {
    ...resolved,
    id: 'default' as const,
    ...pitch,
    cta: undefined,
    usedFallbackCopy: true,
  }
}

/** The plan-name CTA used when neither the caller nor the intent names the job. */
export function genericCtaLabel(product: ScoProduct, options: { upgrade?: boolean } = {}): string {
  return `${options.upgrade ? 'Upgrade to' : 'Get'} ${productLabel(product)}`
}

/** The CTA label for a plan on this wall: an explicit label, the intent's job, then the plan name. */
export function productCtaLabel(
  product: ScoProduct,
  copy: { cta?: string; recommendedProductId: string },
  explicit?: string,
  options: { upgrade?: boolean } = {}
): string {
  return (
    explicit?.trim() ||
    (copy.recommendedProductId === product.productId ? copy.cta?.trim() : undefined) ||
    genericCtaLabel(product, options)
  )
}

/**
 * The cheaper listed plan whose visible lines this product fully contains, so a higher
 * tier can say "Everything in X, plus" and lead with what it adds. Null when nothing nests.
 */
export function includedPlanFor(product: ScoProduct, products: ScoProduct[], intent: PaywallIntent) {
  const price = productPricing(product).recurring ?? productPricing(product).signup
  if (price == null) {
    return null
  }
  // By slug: a higher amount on the same line (15 vs 3 messages a day) still nests.
  const keys = new Set(visiblePaywallFeatures(product, intent).map(feature => feature.key))
  const candidates = products.flatMap(other => {
    const otherPrice = productPricing(other).recurring ?? productPricing(other).signup
    if (other.id === product.id || otherPrice == null || otherPrice >= price) {
      return []
    }
    const otherKeys = visiblePaywallFeatures(other, intent).map(feature => feature.key)
    const nests = otherKeys.length > 0 && otherKeys.every(key => keys.has(key))
    return nests && otherKeys.length < keys.size ? [{ plan: other, size: otherKeys.length }] : []
  })
  return candidates.sort((left, right) => right.size - left.size)[0]?.plan ?? null
}

export type TermChangeDirection = 'upgrade' | 'downgrade' | 'switch'

/** Longer prepaid term is an upgrade. Unknown lengths are a switch, not a guess. */
export function termChangeDirection(fromTermId: string, toTermId: string): TermChangeDirection | null {
  if (!fromTermId || !toTermId || fromTermId === toTermId) {
    return null
  }
  const fromMonths = TERM_MONTHS[fromTermId]
  const toMonths = TERM_MONTHS[toTermId]
  if (!fromMonths || !toMonths) {
    return 'switch'
  }
  return toMonths > fromMonths ? 'upgrade' : 'downgrade'
}

/** Whether a plan tile shows a CTA, and whether that CTA is the member's own plan and term. */
export function productOfferState(
  product: ScoProduct,
  intent: PaywallIntent,
  options: {
    currentProductIds?: string[]
    eligibleProductIds?: string[]
    /**
     * Billing term the member already pays, keyed by productId. A held product is current
     * only when this matches the card's term. Omit the map to keep product-id matching.
     * A `null` term means the subscription did not say — treat the product as current.
     */
    currentTermByProductId?: Record<string, string | null>
  }
) {
  const recommended = isRecommendedProduct(product, intent)
  const holdsProduct = Boolean(product.productId && options.currentProductIds?.includes(product.productId))
  let termChange: TermChangeDirection | null = null
  if (
    holdsProduct &&
    product.productId &&
    options.currentTermByProductId &&
    product.productId in options.currentTermByProductId
  ) {
    const heldTerm = options.currentTermByProductId[product.productId]
    const viewedTerm = product.effectiveTermId || product.termId || null
    if (heldTerm && viewedTerm && heldTerm !== viewedTerm) {
      termChange = termChangeDirection(heldTerm, viewedTerm)
    }
  }
  const current = holdsProduct && !termChange
  const showAction =
    current ||
    Boolean(termChange) ||
    (options.eligibleProductIds
      ? Boolean(product.productId && options.eligibleProductIds.includes(product.productId))
      : intent.id === 'default' || recommended)
  return { current, recommended, showAction, termChange }
}
